import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2, ImagePlus, X, Mic, StopCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Full Respray",
  "Custom Paint Job",
  "Colour Matching",
  "Dent & Scratch Repair",
  "Ceramic Coating",
  "Detailing",
  "Other",
];

const MAX_PHOTOS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", vehicle_model: "", service_type: "", description: "",
  });
  const [photos, setPhotos] = useState([]);       // { file, preview, base64 }
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [highlightService, setHighlightService] = useState(null);
  const fileInputRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  // Listen for service selection from ServicesSection
  useEffect(() => {
    const handleSelectService = (e) => {
      const service = e.detail;
      if (service && SERVICE_OPTIONS.includes(service)) {
        setFormData(prev => ({ ...prev, service_type: service }));
        setHighlightService(service);
        // Remove highlight after animation
        setTimeout(() => setHighlightService(null), 1500);
      }
    };
    
    window.addEventListener('selectService', handleSelectService);
    return () => window.removeEventListener('selectService', handleSelectService);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleServiceSelect = (service) => {
    setFormData((prev) => ({ ...prev, service_type: service }));
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos((prev) => [
          ...prev,
          { file, preview: URL.createObjectURL(file), base64: ev.target.result },
        ]);
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // Voice Recording using Web Speech API
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice input not supported in this browser. Try Chrome or Edge.");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    
    recognition.onstart = () => {
      setIsRecording(true);
      toast.info("Listening... Speak now");
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        }
      }
      
      if (finalTranscript) {
        setFormData(prev => ({
          ...prev,
          description: prev.description + finalTranscript
        }));
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        toast.error(`Voice error: ${event.error}`);
      }
      setIsRecording(false);
    };
    
    recognition.onend = () => {
      setIsRecording(false);
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      toast.success("Voice note captured!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.vehicle_model || !formData.service_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...formData,
        photos: photos.map((p) => p.base64),
      });
      setSubmitted(true);
      toast.success("Quote request submitted! We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try texting us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="quote" data-testid="quote-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#09090b] border border-white/5 p-16 text-center">
            <CheckCircle size={48} className="text-[#3b82f6] mx-auto mb-6" />
            <h3 data-testid="quote-success-heading" className="font-heading text-3xl sm:text-4xl uppercase tracking-wide text-white mb-4">
              Quote Request Sent
            </h3>
            <p className="text-zinc-400 font-body text-base max-w-md mx-auto mb-8">
              Thanks for reaching out. We will review your details and get back to you as soon as possible.
            </p>
            <button
              onClick={() => { setSubmitted(false); setFormData({ name: "", phone: "", email: "", vehicle_model: "", service_type: "", description: "" }); setPhotos([]); }}
              data-testid="quote-submit-another"
              className="border border-white/20 text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-white/5 transition-colors cursor-pointer bg-transparent"
            >
              Submit Another
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" data-testid="quote-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-[1px] bg-white/5">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 bg-[#09090b] p-8 md:p-12 flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[1px] bg-[#3b82f6]" />
                <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Contact</span>
              </div>
              <h2 data-testid="quote-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95] mb-8">
                Get a<br />Quote
              </h2>
              <p className="text-zinc-400 font-body text-base leading-relaxed mb-10">
                Fill out the form and we will get back to you with a quote.
                Or shoot us a text, we are always happy to chat.
              </p>
              <div className="space-y-4 mb-10">
                <a href="sms:0450645926" data-testid="quote-phone-blake" className="flex items-center gap-3 text-white font-mono text-base hover:text-[#3b82f6] transition-colors no-underline">
                  Blake / 0450 645 926
                </a>
                <a href="sms:0421169339" data-testid="quote-phone-harrison" className="flex items-center gap-3 text-white font-mono text-base hover:text-[#3b82f6] transition-colors no-underline">
                  Harrisson / 0421 169 339
                </a>
                <a href="mailto:sales@velocitypaintandpanel.com" data-testid="quote-email" className="flex items-center gap-3 text-white font-mono text-sm hover:text-[#3b82f6] transition-colors no-underline">
                  sales@velocitypaintandpanel.com
                </a>
              </div>
            </div>

            <div className="map-container w-full h-48 mt-auto hidden lg:block border border-white/10 overflow-hidden" style={{ filter: "invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)" }}>
              <iframe
                title="101 Norman St Woolloongabba"
                src="https://www.google.com/maps?q=101+Norman+St,+Woolloongabba+QLD+4102&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="quote-map"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3 bg-[#09090b] p-8 md:p-12"
          >
            <form onSubmit={handleSubmit} data-testid="quote-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-3">Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" data-testid="quote-input-name"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-4 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-3">Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="04XX XXX XXX" data-testid="quote-input-phone"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-4 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-3">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" data-testid="quote-input-email"
                  className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-4 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" />
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-3">Vehicle Make & Model *</label>
                <input type="text" name="vehicle_model" value={formData.vehicle_model} onChange={handleChange} placeholder="e.g. Nissan Silvia S15" data-testid="quote-input-vehicle"
                  className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-4 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm" required />
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-4">Service Type *</label>
                <div className="flex flex-wrap gap-2" data-testid="quote-service-type-group">
                  {SERVICE_OPTIONS.map((service) => (
                    <button type="button" key={service} onClick={() => handleServiceSelect(service)}
                      data-testid={`quote-service-${service.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-4 py-2 text-xs tracking-[0.1em] uppercase font-body border cursor-pointer transition-all duration-300 ${
                        formData.service_type === service 
                          ? "bg-white text-black border-white" 
                          : "bg-transparent text-zinc-500 border-white/10 hover:border-white/30 hover:text-white"
                      } ${highlightService === service ? "ring-2 ring-[#3b82f6] ring-offset-2 ring-offset-[#09090b] scale-105" : ""}`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-3">Tell us more</label>
                <div className="relative">
                  <textarea name="description" value={formData.description} onChange={handleChange} rows={3}
                    placeholder="Any details about the job, colour preferences, damage description, timeline..."
                    data-testid="quote-input-description"
                    className="form-input-glow bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pr-14 text-white font-mono placeholder-zinc-700 w-full transition-colors text-sm resize-none" />
                  {/* Voice input button */}
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    data-testid="quote-voice-btn"
                    className={`absolute right-2 top-2 p-2 border transition-colors cursor-pointer ${
                      isRecording 
                        ? "bg-red-500/20 border-red-500/40 text-red-400 animate-pulse" 
                        : "bg-white/5 border-white/10 text-zinc-500 hover:text-white hover:border-white/30"
                    }`}
                    title={isRecording ? "Stop recording" : "Voice input"}
                  >
                    {isRecording ? <StopCircle size={16} /> : <Mic size={16} />}
                  </button>
                </div>
                {isRecording && (
                  <p className="text-xs text-red-400 mt-1 animate-pulse">Recording... speak clearly</p>
                )}
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-4">
                  Photos <span className="text-zinc-600">(optional, up to {MAX_PHOTOS})</span>
                </label>

                <div className="flex flex-wrap gap-3 items-start">
                  {photos.map((photo, i) => (
                    <div key={i} className="relative w-24 h-24 group" data-testid={`quote-photo-${i}`}>
                      <img src={photo.preview} alt={`Upload ${i + 1}`} className="w-full h-full object-cover border border-white/10" />
                      <button type="button" onClick={() => removePhoto(i)}
                        data-testid={`quote-photo-remove-${i}`}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  {photos.length < MAX_PHOTOS && (
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      data-testid="quote-photo-add"
                      className="w-24 h-24 border border-dashed border-white/10 hover:border-white/30 flex flex-col items-center justify-center gap-1 cursor-pointer bg-transparent transition-colors group"
                    >
                      <ImagePlus size={20} className="text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                      <span className="text-[10px] text-zinc-700 group-hover:text-zinc-500 transition-colors uppercase tracking-wider">Add</span>
                    </button>
                  )}

                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoAdd}
                    className="hidden" data-testid="quote-photo-input" />
                </div>
                <p className="text-zinc-700 font-body text-xs mt-2">Show us the current state of your car to help us quote accurately</p>
              </div>

              <button type="submit" disabled={loading} data-testid="quote-submit-btn"
                className="flex items-center justify-center gap-3 bg-white text-black font-heading text-lg tracking-[0.15em] uppercase px-12 py-4 hover:bg-zinc-200 transition-colors cursor-pointer border-none w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (<><Loader2 size={18} className="animate-spin" />Sending...</>) : (<>Submit Quote<Send size={16} /></>)}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
